import { useMutation, useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect, useRef, useState, Fragment } from 'react'
import type { ChangeEvent } from 'react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { QUERY_KEY } from '../../../constants'

import { toast } from '../../../utils'

import { StoresContext } from '../../../provider/StoreProvider'

import useParamId from '../../../hooks/useParamId'
import { useUserInfo } from '../../../hooks/useUserInfo'

import { getQnaDetail, getQnaChats, registerQnaChats } from '../../../api/qna'

import { Head } from '../../../components/head/Head'
import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { CenterWrap } from '../../../components/common'
import { Input } from '../../../components/input'
import { Text } from '../../../components/text'
import { Header } from '../../../components/header'
import { Spinner } from '../../../components/spinner/Spinner'
import { InfinityScroll } from '../../../components/pagingList/InfinityScroll'
import { HEAD_INFO } from './Qna'

import { Color } from '../../../theme/color'

import { ReactComponent as SendIcon } from '../../../assets/ico/more/send/send_default.svg'
import { ReactComponent as SendActiveIcon } from '../../../assets/ico/more/send/send_active.svg'

export function QnaDetail() {
  const {
    commonStore: { setInvalidAccess },
  } = useContext(StoresContext)

  const [chat, setChat] = useState<string>('')

  const qnaId = useParamId()
  const { isLoading, isLogin, isCheckingLogin } = useUserInfo()
  const queryClient = useQueryClient()

  const qnaDetail = useQuery({
    queryKey: [QUERY_KEY.qna, { qnaId }],
    queryFn: async () => await getQnaDetail(qnaId as number),
    enabled: typeof qnaId === 'number',
  })
  const { data, isPending, isError, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEY.qnaChats, { qnaId }],
    queryFn: async ({ pageParam = 0 }) => await getQnaChats(qnaId as number, { offset: pageParam, size: 50 }),
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.offset + lastPage.size),
    initialPageParam: 0,
    enabled: typeof qnaId === 'number',
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })
  const { mutate } = useMutation({
    mutationFn: registerQnaChats,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.qna] })
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.qnaChats] })
      setChat('')
      scrollToBottom()
    },
    onError: (error) => {
      toast('문의사항 전송에 실패했어요.', error)
    },
  })

  const element = useRef<HTMLDivElement>(document.createElement('div'))

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (qnaDetail.isError && qnaDetail.error) {
      if (isAxiosError(qnaDetail.error)) {
        toast(qnaDetail.error.response?.data.message, qnaDetail.error)
      }
    }
  }, [qnaDetail.isError, qnaDetail.error])

  useEffect(() => {
    scrollToBottom()
  }, [data?.pages, element.current])

  const scrollToBottom = (delay?: number) => {
    setTimeout(() => {
      element.current?.scrollIntoView({ block: 'end', behavior: 'smooth' })
    }, delay ?? 500)
  }

  if (isLoading || qnaDetail.isPending || qnaDetail.isError || isPending || isError) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <Header type="SUB" title="1:1문의내역" />
          <CenterWrap>
            <Spinner />
          </CenterWrap>
        </Wrap>
      </>
    )
  }

  const onChangeInput = ({ currentTarget: { value } }: ChangeEvent<HTMLInputElement>) => {
    setChat(String(value))
  }

  const onRegisterQnaChats = async () => {
    if (typeof qnaId === 'undefined' || !chat || !chat.trim()) return
    mutate({ qnaId, data: { content: chat } })
  }

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="1:1문의내역" />
        <StyledContent>
          <TitleContainer>
            <Text size={18} weight={600}>
              문의 제목
            </Text>
            <TitleWrap>
              <Text size={16} weight={500} lineHeight={24} color="text_secondary">
                {qnaDetail.data.title}
              </Text>
            </TitleWrap>
            <Text size={18} weight={600}>
              문의 내용
            </Text>
          </TitleContainer>
          <ListBackground>
            <ListContainer ref={element}>
              <InfinityScroll reverse={true} length={data.pages.length} isLast={!hasNextPage} next={fetchNextPage}>
                {data.pages.map(({ id, isMine, content, createdDate }, index) => {
                  // 배열 기준 다음 인덱스 : next
                  // 배열 기준 이전 인덱스 : before
                  const currentDate = dayjs(createdDate).format('YYYYMMDD')
                  const nextDate = dayjs(data.pages[index + 1]?.createdDate).format('YYYYMMDD')
                  const currentTime = dayjs(createdDate).format('HHmm')
                  const beforeTime = dayjs(data.pages[index - 1]?.createdDate).format('HHmm')

                  // 같은 날 체크
                  const isSameDate = currentDate === nextDate
                  // 같은 시간:분 체크
                  const isSameTime = beforeTime === currentTime
                  // 같은 유저 체크
                  const isSameUser = data.pages[index - 1]?.isMine === isMine

                  const isShowTime = !isSameUser || index === 0 || !isSameTime

                  return (
                    <Fragment key={id}>
                      <ListItem isMine={isMine}>
                        <ChatWrap isMine={isMine}>
                          <ChatContent>
                            <Text size={14} lineHeight={22} color={isMine ? 'text_white' : 'text_primary'}>
                              {content}
                            </Text>
                          </ChatContent>
                          {isShowTime && (
                            <Date>
                              <Text size={12} color="text_secondary">
                                {dayjs(createdDate).format('HH:mm')}
                              </Text>
                            </Date>
                          )}
                        </ChatWrap>
                      </ListItem>
                      {!isSameDate && (
                        <DateChip>
                          <Text size={12} color="text_secondary">
                            {dayjs(createdDate).format('YYYY.MM.DD')}
                          </Text>
                        </DateChip>
                      )}
                    </Fragment>
                  )
                })}
              </InfinityScroll>
            </ListContainer>
          </ListBackground>
        </StyledContent>
        <SendBoxFooter>
          <Input
            value={chat}
            placeholder="추가 문의 사항을 입력해주세요."
            block={true}
            icon={chat && chat.trim() ? <SendActiveIcon onClick={onRegisterQnaChats} /> : <SendIcon />}
            onChange={onChangeInput}
          />
        </SendBoxFooter>
      </Wrap>
    </>
  )
}

const StyledContent = styled(Content)`
  display: flex;
  flex-direction: column;
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 24px 8px;
  background-color: ${Color.white};
`
const TitleWrap = styled.div`
  width: 100%;
  max-height: 80px;
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${Color.gray_1};
`
const ListBackground = styled.div`
  flex-grow: 0;
  flex-shrink: 1;
  overflow-y: auto;
  height: 100%;
  background-color: ${Color.gray_1};
`

const ListContainer = styled.div`
  div.infinite-scroll-component {
    display: flex;
    flex-direction: column-reverse;
    gap: 8px;
    padding: 24px;
  }
`

const ListItem = styled.div<{ isMine: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ isMine }) => (isMine ? 'flex-end' : 'flex-start')};
`

const ChatContent = styled.div`
  width: fit-content;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  > * {
    user-select: text;
  }
`

const Date = styled.div`
  display: flex;
  justify-content: end;
  margin-bottom: 8px;
`

const ChatWrap = styled.div<{ isMine: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: fit-content;
  max-width: 85%;
  > ${ChatContent} {
    ${({ isMine }) =>
      isMine
        ? `
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
          border-bottom-right-radius: 0;
          border-bottom-left-radius: 16px;
          background-color: ${Color.primary_5};
          `
        : `
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
          border-bottom-right-radius: 16px;
          border-bottom-left-radius: 0;
          background-color: ${Color.white};
        `}
  }
  > ${Date} {
    justify-content: ${({ isMine }) => (isMine ? 'flex-start' : 'flex-end')};
  }
`

const DateChip = styled.div`
  display: inline-block;
  width: fit-content;
  margin: 0 auto 8px;
  padding: 4px 8px;
  border-radius: 50vh;
  background-color: ${Color.white};
`

const SendBoxFooter = styled.div`
  overflow: hidden;
  width: 100%;
  padding: 8px 24px;
  background-color: ${Color.white};
  svg {
    cursor: pointer;
  }
`
